// THE TRIGRAM (UNIFIED-FUNCTION F8) — one similarity function, one set of thresholds.
//
// Shared by the server and both SPAs so the two sides can never disagree. NOTHING here may
// import `pg`, React, or anything from src/server — this module is bundled into the browser.
//
// It is a faithful re-implementation of PostgreSQL `pg_trgm.similarity(a, b)`: the Jaccard
// index of the two padded-word trigram SETS. That matters, because the same comparison runs
// in two places — the server computes a pending proposal's register neighbours in SQL (over
// `idx_revisions_statement_trgm`), and the client computes within-batch twins over the pending
// set it already holds. A twin scored 0.63 by one and 0.58 by the other would be a queue that
// contradicts itself.
//
// pg_trgm's rule, exactly: lower-case, every non-alphanumeric becomes a separator, each word is
// padded to '  word ' (two leading spaces, one trailing), and every 3-character window of the
// padded word is a trigram. Duplicates collapse — it is a SET, so 'the the' and 'the' produce
// the same trigrams.
//
// HONEST LIMIT, to be stated wherever this is rendered: the trigram catches LEXICAL near-twins
// and misses semantic ones. Of the 20 historical `duplicate` refusals in URSC, 2 score ≥ 0.60
// and 7 score ≥ 0.40. It is a filter that never fires falsely, not a detector — which is why
// the collision lane OFFERS a refusal and never performs one.

/** At or above this, the page shows the register row in full dress and offers the refusals. */
export const SIM_HIT = 0.6;

/** At or above this but under HIT, one quiet line: close enough to read, not close enough to act. */
export const SIM_NEAR = 0.4;

/**
 * Under this, nothing is shown at all — and the page SAYS the score was under it, so an empty
 * neighbourhood reads as "new ground", never as "not checked" (B8).
 */
export const SIM_FLOOR = 0.18;

/** pg_trgm's tokenizer: lower-case, non-alphanumerics are separators, empties dropped. */
function words(text: string): string[] {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean);
}

/** The padded-word trigram SET of one string. Exported for tests and for callers that batch. */
export function trigrams(text: string): Set<string> {
  const out = new Set<string>();
  for (const word of words(text)) {
    const padded = `  ${word} `;
    for (let i = 0; i + 3 <= padded.length; i += 1) out.add(padded.slice(i, i + 3));
  }
  return out;
}

/**
 * `pg_trgm.similarity(a, b)` — |A ∩ B| / |A ∪ B|, in [0, 1].
 *
 * Two strings with no trigrams between them (both empty, or both punctuation) are NOT similar:
 * the answer is 0, not 1. Postgres agrees.
 */
export function trigramSimilarity(a: string, b: string): number {
  const A = trigrams(a);
  const B = trigrams(b);
  if (!A.size || !B.size) return 0;
  let shared = 0;
  for (const t of A) if (B.has(t)) shared += 1;
  const union = A.size + B.size - shared;
  return union === 0 ? 0 : shared / union;
}

/** Where a score falls, named once so no surface invents its own boundary. */
export type SimBand = 'hit' | 'near' | 'far';

export function simBand(sim: number): SimBand {
  if (sim >= SIM_HIT) return 'hit';
  if (sim >= SIM_NEAR) return 'near';
  return 'far';
}
